/**
 * WakeUpContainerScreen — First container of a scheduled retreat.
 *
 * M2-UX-P1-013: Shown at the start of a scheduled retreat, before the first bell.
 * The practitioner settles, the system counts down. No interaction.
 * UI-INV-005: Leaf component, props only, no engine imports.
 *
 * System-graph: wake-up-container screen.
 *   state: live (first container)
 *   exits: [live] — automatically on first bell
 *   sensory: { sight: dial-countdown, sound: wake-bell, touch: none, silence: default }
 *
 * Design: begin-end-screens.jsx WakeUpContainerScreen
 */
import styles from "./WakeUpContainerScreen.module.css";
import { calculateCircumference } from "../utils/geometry";

const DIAL_SIZE = 200;
const RADIUS = DIAL_SIZE / 2 - 8;
const CX = DIAL_SIZE / 2;
const CY = DIAL_SIZE / 2;
const CIRCUMFERENCE = calculateCircumference(RADIUS);

type Props = {
  remaining: string;
  remainingFraction: number;
  nextSessionName: string;
  retreatName: string;
};

export function WakeUpContainerScreen({
  remaining,
  remainingFraction,
  nextSessionName,
  retreatName,
}: Props) {
  const angle = -Math.PI / 2 + remainingFraction * 2 * Math.PI;
  const dotX = CX + RADIUS * Math.cos(angle);
  const dotY = CY + RADIUS * Math.sin(angle);

  return (
    <div className={styles.container}>
      <div className={styles.eyebrow}>Getting ready</div>

      <div className={styles.title}>Prepare your space.</div>
      <div className={styles.subtitle}>
        Cushion. Posture. One slow breath. The bell will begin {nextSessionName}
        .
      </div>

      <div className={styles.dial}>
        <svg
          width={DIAL_SIZE}
          height={DIAL_SIZE}
          aria-label={`${remaining} remaining`}
        >
          <circle
            cx={CX}
            cy={CY}
            r={RADIUS}
            fill="none"
            stroke="var(--rc-ink-4)"
            strokeWidth="1.4"
          />
          <circle
            cx={CX}
            cy={CY}
            r={RADIUS}
            fill="none"
            stroke="var(--rc-ink)"
            strokeWidth="2"
            strokeDasharray={`${CIRCUMFERENCE * remainingFraction} ${CIRCUMFERENCE}`}
            transform={`rotate(-90 ${CX} ${CY})`}
            strokeLinecap="round"
          />
          {remainingFraction > 0 && (
            <circle cx={dotX} cy={dotY} r="4" fill="var(--rc-clay)" />
          )}
        </svg>

        <div className={styles.dialCenter}>
          <div className={styles.dialTime}>{remaining}</div>
          <div className={styles.dialUnit}>remaining</div>
        </div>
      </div>

      <div className={styles.retreatName}>{retreatName}</div>
    </div>
  );
}
